import { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

import { Box } from 'components/Box';
import { Divider } from 'components/Divider';
import { SearchTile } from 'components/Search/SearchTile';
import { colors, gutterWidth } from 'theme';
import {
  SEARCH_HIT_IMAGE_WIDTH,
  SEARCH_HIT_IMAGE_WIDTH_SMALL,
} from 'utils/constants/general';
import { range, uuid } from 'utils/functional';

const whoosh = keyframes`
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
`;
export const Line = styled(Box)`
  position: relative;
  overflow: hidden;
  -webkit-mask-image: radial-gradient(white, black);
  border-radius: ${(props) => (props.borderRadius ? undefined : '3px')};
  &::before {
    content: '';
    position: absolute;
    left: 0;
    width: 200%;
    height: 100%;
    background: linear-gradient(
      to right,
      ${colors.selectionGrey} 0%,
      ${colors.calloutGrey} 50%,
      ${colors.selectionGrey} 100%
    );
    background-size: 50% 100%;
    animation: ${whoosh} 1s linear infinite;
  }
`;
export function MockPostResults() {
  return (
    <>
      <Box>
        <Line pb="56.25%" borderRadius={24} />
        <Line mt={36} height={28} width="75%" />
        <Line mt={24} height={12} />
      </Box>

      <Divider light mt={48} my={72} />

      <Box display="flex" flexWrap="wrap" mx={-24} my={24}>
        {new Array(10).fill(null).map<ReactNode>((item, index) => (
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} width={[1, null, 1 / 2]} flex="0 0 auto" p={24}>
            <Line pb="56.25%" borderRadius={24} />
            <Line mt={28} height={20} width="75%" />
            <Line mt={12} height={12} />
          </Box>
        ))}
      </Box>
    </>
  );
}

export function PostSearchLoadingSection() {
  return (
    <Box display="flex" pt={54} flexWrap="wrap">
      <Box
        px={[gutterWidth / 2, null, null, gutterWidth]}
        width={[1, null, null, 1 / 3]}
        display={['none', null, null, 'block']}
      >
        <Line height={40} width="75%" />
        <Line height={40} width="54%" mt={12} />
        <Box borderBottom={`1px solid ${colors.selectionGrey}`} pb={32} pt={16}>
          <Line width={60} height={12} />
        </Box>
        {new Array(6).fill(null).map((item, index) => (
          <Box
            py={27} // eslint-disable-next-line react/no-array-index-key
            key={index}
            borderBottom={`1px solid ${colors.selectionGrey}`}
          >
            <Line width={`${20 + ((index + 1) % 2) * 20}%`} height={9} />
          </Box>
        ))}
      </Box>
      <Box
        px={[gutterWidth / 2, null, null, gutterWidth]}
        width={[1, null, null, 2 / 3]}
      >
        <MockPostResults />
      </Box>
    </Box>
  );
}
export const MockResults = ({ hideLabel }: { hideLabel?: boolean }) =>
  range(10).map<ReactNode>(() => (
    <SearchTile key={uuid()}>
      <Box display={['block', 'flex']}>
        <Box position="relative" flex="0 0 auto" pr={[0, 30]} pb={[24, 0]}>
          <Line
            width={[
              SEARCH_HIT_IMAGE_WIDTH_SMALL,
              null,
              null,
              null,
              SEARCH_HIT_IMAGE_WIDTH,
            ]}
            height={[
              SEARCH_HIT_IMAGE_WIDTH_SMALL,
              null,
              null,
              null,
              SEARCH_HIT_IMAGE_WIDTH,
            ]}
            borderRadius="50%"
          />
        </Box>
        <Box
          position="relative"
          width="100%"
          flex="1 1 auto"
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {!hideLabel && (
            <Box display="flex" alignItems="center" mb={32}>
              <Line width={16} height={16} mr={12} borderRadius="50%" />
              <Line width={120} height={10} />
            </Box>
          )}
          <Line width="82.5%" height={20} />
          <Line width="95%" height={12} mt={16} />
          <Line width={160} height={8} mt={32} />
        </Box>
      </Box>
    </SearchTile>
  ));

type Props = {
  hideLabel?: boolean;
};

export function SearchLoadingSection({ hideLabel }: Props) {
  return (
    <Box display="flex" flexWrap="wrap">
      <Box
        px={[gutterWidth / 2, null, null, gutterWidth]}
        width={[1, null, null, 1 / 3]}
        display={['none', null, null, 'block']}
      >
        <Box borderBottom={`1px solid ${colors.selectionGrey}`} pb={32} pt={16}>
          <Line width={60} height={12} />
        </Box>
        {range(6).map((index) => (
          <Box
            py={27}
            key={index}
            borderBottom={`1px solid ${colors.selectionGrey}`}
          >
            <Line width={`${20 + ((index + 1) % 2) * 20}%`} height={9} />
          </Box>
        ))}
      </Box>
      <Box
        px={[gutterWidth / 2, null, null, gutterWidth]}
        width={[1, null, null, 2 / 3]}
      >
        <Box mb={32} pt={12}>
          <Box width={1} display="flex" justifyContent="space-between">
            <Line width="25%" height={12} />
            <Line width="40%" height={12} />
          </Box>
          <Divider mt={36} mb={0} light />
        </Box>
        {/* @ts-expect-error TS(2786): 'MockResults' cannot be used as a JSX component. */}
        <MockResults hideLabel={hideLabel} />
      </Box>
    </Box>
  );
}
